import { useEffect, useRef, useState } from 'react'

import { usePractitionersOus } from 'api/queries/marketing-capabilities/practitioners/usePractitionersOUs'
import { useOnboardingFilters } from 'api/queries/marketing-capabilities/resources/useOnboardingFilters'
import { useSubmitUploadResource } from 'api/queries/marketing-capabilities/resources/useSubmitUploadResource'
import { useSkills } from 'api/queries/marketing-capabilities/skills/useSkills'
import crossIcon from 'assets/images/explore-learnings/modal-cross.svg'
import { Option } from 'pages/exploreLearnings/types/uploadResource.types'
import { useToast } from 'utils/toast'

import { JOB_FAMILY_OPTIONS } from '../../constants/jobFamily.constants'
import { DESCRIPTION_MAX_CHARS } from '../../constants/uploadResource.constants'
import { useUploadResourceForm } from '../../hooks/useUploadResourceForm'
import { buildPayload } from '../../utils/uploadResource.helpers'
import { Button } from '../Button'
import { FormInput } from '../FormInput'
import { FormTextarea } from '../FormTextarea'
import { SelectMultiple } from '../SelectMultiple'
import { SelectSingle } from '../SelectSingle'
import { Toggle } from '../Toggle'

export interface UploadResourceModalProps {
  onClose: () => void
  onSuccess?: () => void
}

export const UploadResourceModal = ({ onClose, onSuccess }: UploadResourceModalProps): JSX.Element => {
  const { form, errors, handleChange, handleDescriptionChange, handleGlobalToggle, validateBeforeSubmit, hasAnyValue } =
    useUploadResourceForm()

  const { data: skillList } = useSkills(true)
  const { data: resourceTypeList } = useOnboardingFilters()
  const { data: ouList } = usePractitionersOus()
  const { mutateAsync: submitResource, isPending } = useSubmitUploadResource()
  const { showToast } = useToast()
  const modalBodyRef = useRef<HTMLDivElement>(null)
  const [hasShadow, setHasShadow] = useState(false)
  const [showFooterShadow, setShowFooterShadow] = useState(false)

  const skillOptions =
    (Array.isArray(skillList) ? skillList : skillList?.data)?.map((skill: unknown) => ({
      key: (skill as { name: string }).name,
      value: (skill as { id: string }).id,
    })) || []

  const resourceTypeOptions =
    resourceTypeList?.map((option: Option) => ({
      key: option.name,
      value: option.id,
    })) || []

  const ouOptions =
    ouList?.map((ou: Option) => ({
      key: ou.name,
      value: ou.id,
    })) || []

  const handleSubmit = async (): Promise<void> => {
    const errs = validateBeforeSubmit()
    if (Object.keys(errs).length > 0) return

    if (typeof window !== 'undefined' && !navigator.onLine) {
      showToast({
        type: 'error',
        message: 'Oops! Something went wrong when submitting the file, please try again.',
      })
      return
    }

    onSuccess?.()
    await submitResource(buildPayload(form, skillOptions, ouOptions))
  }

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [])

  // Handle header shadow - show when scrolled down
  const handleScroll = () => {
    const el = modalBodyRef.current
    if (!el) return
    setHasShadow(el.scrollTop > 0)
  }

  const updateFooterShadow = () => {
    const body = modalBodyRef.current
    if (!body) return

    const scrollHeight = body.scrollHeight
    const scrollTop = body.scrollTop
    const clientHeight = body.clientHeight

    // Method 1: Check if content overflows
    const isOverflowing = scrollHeight > clientHeight

    if (!isOverflowing) {
      setShowFooterShadow(false)
      return
    }

    const distanceFromBottom = scrollHeight - scrollTop - clientHeight
    const hasHiddenContent = distanceFromBottom > 5

    setShowFooterShadow(hasHiddenContent)
  }

  useEffect(() => {
    const initTimer = setTimeout(() => {
      updateFooterShadow()
      handleScroll()
    }, 0)

    return () => clearTimeout(initTimer)
  }, [])

  useEffect(() => {
    const body = modalBodyRef.current
    if (!body) return

    const resizeObserver = new ResizeObserver(() => {
      updateFooterShadow()
    })

    resizeObserver.observe(body)

    const modalContainer = body.closest('.upload-resource-modal')
    if (modalContainer) {
      resizeObserver.observe(modalContainer)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    const handleWindowResize = () => {
      updateFooterShadow()
    }

    window.addEventListener('resize', handleWindowResize)
    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  const handleBodyScroll = () => {
    handleScroll()
    updateFooterShadow()
  }

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50"
      onClick={e => e.stopPropagation()}
    >
      <div className="relative bg-white rounded-xl w-[1016px] max-h-[90vh] shadow-xl upload-resource-modal">
        {/* Header with shadow on scroll */}
        <div
          className={`sticky top-0 bg-white pt-6 z-80 px-6 rounded-t-[12px] transition-shadow duration-200
            ${hasShadow ? 'shadow-md shadow-[0_4px_16px_0_rgba(0,0,0,0.08)]' : ''}`}
        >
          <button
            className="absolute top-9 right-6 text-gray-500 hover:text-gray-800 transition"
            onClick={onClose}
            aria-label="Close"
          >
            <img src={crossIcon} alt="Close" className="self-start cursor-pointer" />
          </button>
          <h2 className="mb-1 headline-m">Upload resource</h2>
          <p className="pb-4 text-gray-500">
            Heads up! Make sure open access is granted to the organization before linking, otherwise they will not be
            able to access the content of the resource.
          </p>
        </div>

        {/* Modal body with scroll */}
        <div
          ref={modalBodyRef}
          className="py-10 flex flex-col gap-5 max-h-[440px] px-6 overflow-y-auto scroller"
          onScroll={handleBodyScroll}
        >
          {/* Row 1: Title + Link */}
          <div className="flex gap-4">
            <FormInput
              label="Resource Title"
              type="text"
              placeholder="Give the resource a title"
              value={form.title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('title', e.target.value)}
              onClear={() => handleChange('title', '')}
              error={errors.title}
            />
            <FormInput
              label="Resource Link"
              type="text"
              placeholder="Paste the link here"
              value={form.link}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('link', e.target.value)}
              onClear={() => handleChange('link', '')}
              error={errors.link}
            />
          </div>

          {/* Row 2: Skills + Resource Type */}
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block subtitle-m mb-2 text-neutral-80">Skills Connected To</label>
              <SelectMultiple
                options={skillOptions}
                selectValue={form.skills}
                onChange={(skills: string[]) => handleChange('skills', skills)}
                placeholder="Select skills"
                searchPlaceholder="E.g. Science"
                hasError={!!errors.skills}
                selectWidth="100%"
                dropdownWidth="100%"
              />
              {errors.skills && <p className="text-error mt-1">{errors.skills}</p>}
            </div>

            <div className="flex-1">
              <label className="block subtitle-m mb-2 text-neutral-80">Resource Type</label>
              <SelectSingle
                selectValue={form.resourceType}
                onChange={(value: string) => handleChange('resourceType', value)}
                options={resourceTypeOptions}
                placeholder="Select an option"
                hasError={!!errors.resourceType}
                selectWidth="100%"
                dropdownWidth="100%"
              />
              {errors.resourceType && <p className="text-error mt-1">{errors.resourceType}</p>}
            </div>
          </div>

          {/* Row 3: Geography + Job Family */}
          <div className="flex gap-4">
            <Toggle
              label="Geography"
              toggleLabel="Global"
              isChecked={form.isGlobal}
              onToggle={handleGlobalToggle}
              error={errors.ou}
            >
              <SelectSingle
                selectValue={form.ou}
                onChange={(value: string) => handleChange('ou', value)}
                options={ouOptions}
                placeholder="Select OU"
                disabled={form.isGlobal}
                hasError={!!errors.ou && !form.isGlobal}
                selectWidth="100%"
                dropdownWidth="100%"
              />
            </Toggle>

            <div className="flex-1">
              <label className="block subtitle-m mb-2 text-neutral-80">Job Family</label>
              <SelectSingle
                selectValue={form.jobFamily}
                onChange={(value: string) => handleChange('jobFamily', value)}
                options={JOB_FAMILY_OPTIONS}
                placeholder="Select a team/job family"
                hasError={!!errors.jobFamily}
                selectWidth="100%"
                dropdownWidth="100%"
              />
              {errors.jobFamily && <p className="text-error mt-1">{errors.jobFamily}</p>}
            </div>
          </div>

          {/* Row 4: Description */}
          <FormTextarea
            label="Description"
            labelSuffix="(Optional)"
            placeholder="Enter a description for the resource"
            value={form.description}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleDescriptionChange(e.target.value)}
            rows={4}
            error={errors.description}
            charCount={form.description.length}
            maxChars={DESCRIPTION_MAX_CHARS}
          />
        </div>

        {/* Footer with shadow when content is hidden below */}
        <div
          className={`flex justify-end py-6 sticky bottom-0 bg-white px-6 rounded-b-[12px] transition-shadow duration-200
            ${showFooterShadow ? 'shadow-[0_-4px_16px_0_rgba(0,0,0,0.08)]' : ''}`}
        >
          <Button
            onClick={handleSubmit}
            text="Submit to review"
            disabled={!hasAnyValue || isPending}
            className="h-10"
          />
        </div>
      </div>
    </div>
  )
}
